import type { Fleet, FuscaFleetAddress, FuscaFleetAddressType, FuscaFleetInfo, PageableContentAPI, UUID } from '@cobliteam/common-types';
import type { AxiosResponse } from 'axios';
import camelcaseKeys from 'camelcase-keys';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { Configuration } from '../../request/Types';
import type { FleetAPI } from './APITypes';

export class FuscaFleetRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public getFleets = async (): Promise<Fleet[]> => {
    const requestData = (page: number) => ({
      endpoint: `/fleets?size=10000&page=${page}`,
      method: HttpMethod.GET,
    });

    const fetchFleets = async (fleets: FleetAPI[] = [], page = 0): Promise<FleetAPI[]> => {
      const response: AxiosResponse<PageableContentAPI<FleetAPI>> = await this.requester.request(requestData(page));
      const newFleets = [...fleets, ...response.data.content];
      if (response.data.last) {
        return newFleets;
      }
      return fetchFleets(newFleets, response.data.number + 1);
    };

    const fleets = await fetchFleets();
    const data = fleets.map((fleetApi) => camelcaseKeys(fleetApi, { deep: true }));

    return data;
  };

  public getFleetInfo = async (fleetId: UUID, addressType?: FuscaFleetAddressType): Promise<FuscaFleetInfo> => {
    const requestData = {
      endpoint: `/fleets/${fleetId}?addressType=${addressType}`,
      method: HttpMethod.GET,
    };

    const response = await this.requester.request(requestData);

    const data = camelcaseKeys(response.data, { deep: true });

    const fleetInfo: FuscaFleetInfo = {
      id: data.id,
      name: data.name,
      shipping: {
        address: data.shipping?.address,
        contactEmail: data.shipping?.contactEmail,
        contactPhone: data.shipping?.contactPhone,
      },
      addresses: camelcaseKeys(data.addresses, { deep: true }),
    };

    return fleetInfo;
  };

  public updateFleetAddress = async (fleetId: UUID, fleetAddress: FuscaFleetAddress) => {
    const requestData = {
      endpoint: `/fleets/${fleetId}/addresses/${fleetAddress.id}`,
      method: HttpMethod.PUT,
      body: JSON.stringify({
        phone: fleetAddress.phone,
        email: fleetAddress.email,
        uf: fleetAddress.uf,
        city: fleetAddress.city,
        zip_code: fleetAddress.zipCode,
        neighborhood: fleetAddress.neighborhood,
        street: fleetAddress.street,
        number: fleetAddress.number,
        complement: fleetAddress.complement,
        address_type: fleetAddress.addressType,
      }),
    };

    await this.requester.request(requestData);
  };

  public createFleetAddress = async (fleetId: UUID, fleetAddress: FuscaFleetAddress) => {
    const requestData = {
      endpoint: `/fleets/${fleetId}/addresses`,
      method: HttpMethod.POST,
      body: JSON.stringify({
        phone: fleetAddress.phone,
        email: fleetAddress.email,
        uf: fleetAddress.uf,
        city: fleetAddress.city,
        zip_code: fleetAddress.zipCode,
        neighborhood: fleetAddress.neighborhood,
        street: fleetAddress.street,
        number: fleetAddress.number,
        complement: fleetAddress.complement,
        address_type: fleetAddress.addressType,
      }),
    };

    await this.requester.request(requestData);
  };
}

export default FuscaFleetRequester;
